// Pure helpers for the AI analysis jobs hierarchy. No Vue, no store, no '@/' imports:
// these run under `node --test` as well as in the browser bundle.
import { toUiStatus, toUtcIso } from './findingsMetrics.js';

export const JOB_TYPES = Object.freeze({
  FULL_SCAN: 'FullScan',
  // All snaps of one tag analysed together (the API runs them in rounds of up to 4 and merges them).
  AVA_INSPECT: 'AvaInspect',
});

export const JOB_GROUP_KIND = Object.freeze({ SCANS: 'scans', AVA_INSPECT: 'avaInspect' });

export const SCANS_GROUP_ID = 'scans-jobs-group';
export const SCANS_GROUP_TITLE = 'Scans Jobs Group';


export function splitImagesIds(csv) {
  return String(csv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

// Root `getImages` (state.projects) is filled only by Admin → Projects; on the Integrity Hub route the
// AI Inspect image list (`AI_INSPECT/IMAGES`) is the populated source. Union by id, primary wins.
export function mergeImages(primary, secondary) {
  const byId = new Map();
  [...(secondary || []), ...(primary || [])].forEach((img) => {
    if (img && img.id != null) byId.set(img.id, img);
  });
  return [...byId.values()];
}

const JOB_DATE_FIELDS = Object.freeze(['createDate', 'updateDate']);

/** New job object as the store keeps it: live jobs get the full-scan type the API implies and UTC-pinned dates. */
export function normalizeJob(job) {
  if (!job) return job;
  return {
    ...job,
    jobType: job.jobType || JOB_TYPES.FULL_SCAN,
    ...Object.fromEntries(JOB_DATE_FIELDS.filter((k) => job[k] != null).map((k) => [k, toUtcIso(job[k])])),
  };
}

function scanNames(job, imagesById) {
  return splitImagesIds(job.imagesIds).map((id) => imagesById?.[id]?.name || id);
}

function jobTitle(job, imagesById) {
  const names = scanNames(job, imagesById);
  if (job.jobType === JOB_TYPES.AVA_INSPECT) {
    const count = (job.snapIds || []).length;
    return `AVA Inspect · ${count} ${count === 1 ? 'snap' : 'snaps'}`;
  }
  if (job.jobType === JOB_TYPES.FULL_SCAN && names.length === 1) {
    return names[0];
  }
  return `Run on ${names.length} scan${names.length === 1 ? '' : 's'}`;
}

function toRow(job, imagesById) {
  return {
    id: job.id,
    title: jobTitle(job, imagesById),
    scanNames: scanNames(job, imagesById),
    imagesIds: splitImagesIds(job.imagesIds),
    jobType: job.jobType || JOB_TYPES.FULL_SCAN,
    status: job.status,
    uiStatus: toUiStatus(job.status),
    createDate: job.createDate,
    createdBy: job.createdBy || null,
    findingsCount: job.analysisResult?.numberOfAnomaliesFound ?? null,
    avaInspectId: job.avaInspectId || null,
    runId: job.runId || null,
  };
}

const newestFirst = (a, b) => String(b.createDate || '').localeCompare(String(a.createDate || ''));

/** Two-level tree: [scans group?, ...one group per AVA Inspect]. Never mutates inputs. */
export function buildJobTree(jobs, { avaInspectsById = {}, imagesById = {} } = {}) {
  const rows = (jobs || []).map((j) => toRow(j, imagesById)).sort(newestFirst);
  const scans = rows.filter((r) => !r.avaInspectId);
  const byInspect = rows.filter((r) => r.avaInspectId).reduce((acc, r) => {
    acc[r.avaInspectId] = [...(acc[r.avaInspectId] || []), r];
    return acc;
  }, {});

  const groups = [];
  if (scans.length) {
    groups.push({
      id: SCANS_GROUP_ID,
      title: SCANS_GROUP_TITLE,
      subtitle: '',
      kind: JOB_GROUP_KIND.SCANS,
      children: scans,
    });
  }
  Object.keys(byInspect).forEach((tagId) => {
    const tag = avaInspectsById[tagId];
    groups.push({
      id: tagId,
      title: tag?.name || tag?.title || `AVA Inspect ${tagId}`,
      subtitle: tag?.masterTag?.name || '',
      kind: JOB_GROUP_KIND.AVA_INSPECT,
      children: byInspect[tagId],
    });
  });
  return groups;
}
